import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useMemo } from 'react';

const METRICS = [
  { key: 'mood', label: 'Mood', color: '#3B82F6', fillColor: '#3B82F620' },
  { key: 'energy', label: 'Energy', color: '#10B981', fillColor: '#10B98120' },
  { key: 'confidence', label: 'Confidence', color: '#8B5CF6', fillColor: '#8B5CF620' },
  { key: 'focus', label: 'Focus', color: '#F59E0B', fillColor: '#F59E0B20' },
  { key: 'performance_rating', label: 'Performance', color: '#EC4899', fillColor: '#EC489920' },
];

function rollingAvg(data, key, window = 7) {
  return data.map((_, i) => {
    const start = Math.max(0, i - window + 1);
    const slice = data.slice(start, i + 1);
    return +(slice.reduce((s, d) => s + (d[key] || 0), 0) / slice.length).toFixed(2);
  });
}

export default function RollingAverageTrend({ individualTrends }) {
  const smoothed = useMemo(() => {
    if (!individualTrends || individualTrends.length < 7) return [];
    return individualTrends.map((d, i) => {
      const entry = { date: d.date };
      METRICS.forEach(m => {
        entry[m.key] = rollingAvg(individualTrends, m.key, 7)[i];
      });
      return entry;
    });
  }, [individualTrends]);

  if (smoothed.length < 7) return null;

  return (
    <div data-testid="rolling-average-trend" className="bg-white rounded-xl shadow-lg p-6 border border-emerald-100">
      <h2 className="text-lg font-bold text-gray-900 mb-1">7-Day Rolling Average Trends</h2>
      <p className="text-xs text-gray-500 mb-4">Smoothed trends eliminate daily noise and reveal the true direction of your wellness</p>
      <ResponsiveContainer width="100%" height={320}>
        <AreaChart data={smoothed}>
          <defs>
            {METRICS.map(m => (
              <linearGradient key={m.key} id={`grad-${m.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={m.color} stopOpacity={0.15} />
                <stop offset="95%" stopColor={m.color} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 10 }}
            tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          />
          <YAxis domain={[0, 10]} tick={{ fontSize: 10 }} />
          <Tooltip
            labelFormatter={(d) => new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            formatter={(val, name) => [`${val}`, name]}
            contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e7eb' }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {METRICS.map(m => (
            <Area
              key={m.key}
              type="monotone"
              dataKey={m.key}
              name={m.label}
              stroke={m.color}
              fill={`url(#grad-${m.key})`}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
